import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Divider, Dropdown, Input, Modal, Popover, Select, Tooltip, message } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBold,
  faCheckSquare,
  faCode,
  faCopy,
  faEye,
  faEyeSlash,
  faHighlighter,
  faItalic,
  faKey,
  faLink,
  faListOl,
  faListUl,
  faMinus,
  faEllipsis,
  faExpand,
  faMagnifyingGlassMinus,
  faMagnifyingGlassPlus,
  faMagnifyingGlass,
  faPen,
  faQuoteLeft,
  faRedo,
  faRotateLeft,
  faStrikethrough,
  faTable,
  faUnderline,
  faUndo,
} from '@fortawesome/pro-solid-svg-icons';
import { Editor, EditorContent, NodeViewContent, NodeViewWrapper, ReactNodeViewRenderer, useEditor, useEditorState } from '@tiptap/react';
import { Node, mergeAttributes } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Collaboration from '@tiptap/extension-collaboration';
import CollaborationCaret from '@tiptap/extension-collaboration-caret';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import Highlight from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import { Table, TableCell, TableHeader, TableRow } from '@tiptap/extension-table';
import CodeBlock from '@tiptap/extension-code-block';
import FindAndReplace from '@tiptap/extension-find-and-replace';
import { Fragment, Slice } from '@tiptap/pm/model';
import type * as Y from 'yjs';
import type { Awareness } from 'y-protocols/awareness';
import { deleteNoteSecretBlock, recordNoteSecretCopy, revealNoteSecretBlock, saveNoteSecretBlock } from '../../services/notes';
import { NoteBlock } from '../../types';
import { looksLikeMarkdown, MAX_MARKDOWN_FILE_BYTES, parseMarkdownNote } from './markdownImport';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import NoteFindReplace from './NoteFindReplace';

interface SecretNodeOptions { noteId: string }

const SecretNodeView: React.FC<any> = ({ node, updateAttributes, editor, extension }) => {
  const noteId = (extension.options as SecretNodeOptions).noteId;
  const [label, setLabel] = useState(node.attrs.label || 'Protected value');
  const [draft, setDraft] = useState('');
  const [draftVisible, setDraftVisible] = useState(false);
  const [revealed, setRevealed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savingValues = useRef(new Set<string>());
  const editable = useEditorState({ editor, selector: ({ editor: current }) => current.isEditable });

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
    if (saveTimer.current) clearTimeout(saveTimer.current);
  }, []);

  const save = async (value = draft) => {
    if (!value || savingValues.current.has(value)) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    savingValues.current.add(value);
    try {
      await saveNoteSecretBlock(noteId, node.attrs.blockId, label.trim() || 'Protected value', value);
      updateAttributes({ label, hasValue: true });
      setDraft(current => current === value ? '' : current);
      setDraftVisible(false);
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Could not save encrypted value');
    } finally { savingValues.current.delete(value); }
  };

  useEffect(() => {
    if (!editable || !draft) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => save(draft), 700);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  // `save` intentionally follows the current draft captured by this effect.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, editable]);

  const reveal = async () => {
    setBusy(true);
    try {
      const value = await revealNoteSecretBlock(noteId, node.attrs.blockId);
      setRevealed(value);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setRevealed(null), 30000);
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Could not reveal encrypted value');
    } finally { setBusy(false); }
  };

  const copy = async () => {
    setBusy(true);
    try {
      const value = draft || revealed || await revealNoteSecretBlock(noteId, node.attrs.blockId);
      await navigator.clipboard.writeText(value);
      await recordNoteSecretCopy(noteId).catch(() => undefined);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Could not copy protected value');
    } finally { setBusy(false); }
  };

  return (
    <NodeViewWrapper className="library-inline-secret" data-drag-handle>
      <div className="library-inline-secret-heading" contentEditable={false}>
        {editable ? (
          <Input
            variant="borderless"
            value={label}
            onChange={event => setLabel(event.target.value)}
            onBlur={() => updateAttributes({ label: label.trim() || 'Protected value' })}
            placeholder="Secret label"
          />
        ) : <strong>{node.attrs.label || 'Protected value'}</strong>}
      </div>
      <div className="library-inline-secret-value" contentEditable={false}>
        {revealed !== null && !draft ? (
          <>
            <Input value={revealed} readOnly />
            <Tooltip title="Hide"><Button aria-label="Hide protected value" type="text" icon={<FontAwesomeIcon icon={faEyeSlash} />} onClick={() => setRevealed(null)} /></Tooltip>
          </>
        ) : editable ? (
          <>
            <Input
              className={`library-protected-input ${draftVisible ? 'is-visible' : ''}`}
              type="text"
              value={draft}
              onChange={event => setDraft(event.target.value)}
              onBlur={() => { if (draft) save(draft); }}
              autoComplete="off"
              data-1p-ignore="true"
              data-bwignore="true"
              data-form-type="other"
              data-lpignore="true"
              placeholder={node.attrs.hasValue ? '••••••••••••••••' : 'Type a protected value…'}
            />
            {draft ? (
              <Tooltip title={draftVisible ? 'Hide' : 'Reveal'}><Button aria-label={draftVisible ? 'Hide protected value' : 'Reveal protected value'} type="text" icon={<FontAwesomeIcon icon={draftVisible ? faEyeSlash : faEye} />} onMouseDown={event => event.preventDefault()} onClick={() => setDraftVisible(value => !value)} /></Tooltip>
            ) : node.attrs.hasValue ? (
              <Tooltip title="Reveal"><Button aria-label="Reveal protected value" loading={busy} type="text" icon={<FontAwesomeIcon icon={faEye} />} onClick={reveal} /></Tooltip>
            ) : null}
          </>
        ) : (
          <>
            <code>••••••••••••••••••••••••</code>
            <Tooltip title="Reveal"><Button aria-label="Reveal protected value" loading={busy} disabled={!node.attrs.hasValue} type="text" icon={<FontAwesomeIcon icon={faEye} />} onClick={reveal} /></Tooltip>
          </>
        )}
        {(node.attrs.hasValue || draft) && <Tooltip title={copied ? 'Copied' : 'Copy'}><Button aria-label="Copy protected value" loading={busy} type="text" icon={<FontAwesomeIcon icon={faCopy} />} onClick={copy} /></Tooltip>}
      </div>
    </NodeViewWrapper>
  );
};

const createSecretBlock = (noteId: string) => Node.create<SecretNodeOptions>({
  name: 'secretBlock',
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,
  addOptions: () => ({ noteId }),
  addAttributes() {
    return {
      blockId: { default: null },
      label: { default: 'Protected value' },
      hasValue: { default: false },
    };
  },
  parseHTML() { return [{ tag: 'div[data-note-secret]' }]; },
  renderHTML({ HTMLAttributes }) { return ['div', mergeAttributes(HTMLAttributes, { 'data-note-secret': '' })]; },
  addNodeView() { return ReactNodeViewRenderer(SecretNodeView); },
});

const CodeBlockNodeView: React.FC<any> = ({ node }) => {
  const [copied, setCopied] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const isHtml = String(node.attrs.language || '').toLowerCase() === 'html';
  const previewDocument = `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src https: data:; media-src https: data:; style-src 'unsafe-inline'; font-src https: data:; connect-src 'none'; frame-src 'none'; form-action 'none'; base-uri 'none'"><meta name="viewport" content="width=device-width,initial-scale=1"><style>html{color-scheme:light}body{margin:0;padding:24px;color:#171717;font:15px/1.55 system-ui,sans-serif;background:#fff}img,video{max-width:100%;height:auto}</style></head><body>${node.textContent}</body></html>`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(node.textContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { message.error('Clipboard access was denied'); }
  };
  return (
    <NodeViewWrapper className="library-code-block">
      <div className="library-code-block-heading" contentEditable={false}>
        <span>{isHtml ? 'HTML code block' : 'Code block'}</span>
        <div className="library-code-block-actions">
          {isHtml && <Button size="small" type="text" icon={<FontAwesomeIcon icon={faEye} />} onClick={() => setPreviewOpen(true)}>Preview</Button>}
          <Button size="small" type="text" icon={<FontAwesomeIcon icon={faCopy} />} onClick={copy}>{copied ? 'Copied' : 'Copy'}</Button>
        </div>
      </div>
      <pre><NodeViewContent as="code" /></pre>
      <Modal title="HTML preview · sandboxed" open={previewOpen} width={1100} footer={null} onCancel={() => setPreviewOpen(false)} destroyOnHidden>
        <p className="library-preview-safety">Scripts, forms, navigation, frames, and app access are disabled in this preview.</p>
        <iframe className="library-html-preview-frame" title="Sandboxed HTML preview" sandbox="" referrerPolicy="no-referrer" srcDoc={previewDocument} />
      </Modal>
    </NodeViewWrapper>
  );
};

const CopyableCodeBlock = CodeBlock.extend({
  addNodeView() { return ReactNodeViewRenderer(CodeBlockNodeView); },
});

const RichLink = Link.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      title: { default: null, parseHTML: element => element.getAttribute('title'), renderHTML: attributes => attributes.title ? { title: attributes.title } : {} },
    };
  },
});

const CitationNodeView: React.FC<any> = ({ node, updateAttributes, editor }) => {
  const editable = useEditorState({ editor, selector: ({ editor: current }) => current.isEditable });
  const [citationKey, setCitationKey] = useState(node.attrs.citationKey || '');
  const [suffix, setSuffix] = useState(node.attrs.suffix || '');
  const content = <div className="library-atom-form">
    <Input size="small" value={citationKey} onChange={event => setCitationKey(event.target.value)} placeholder="Citation key" />
    <Input size="small" value={suffix} onChange={event => setSuffix(event.target.value)} placeholder="Page or section (optional)" />
    <Button size="small" type="primary" onClick={() => updateAttributes({ citationKey: citationKey.trim(), suffix: suffix.trim() || null })}>Save</Button>
  </div>;
  const label = `[@${node.attrs.citationKey}${node.attrs.suffix ? `, ${node.attrs.suffix}` : ''}]`;
  return <NodeViewWrapper as="span" className="library-citation" contentEditable={false}>
    {editable ? <Popover content={content} trigger="click"><button type="button" className="library-inline-atom-button" aria-label="Edit citation">{label}</button></Popover> : label}
  </NodeViewWrapper>;
};

const Citation = Node.create({
  name: 'citation', group: 'inline', inline: true, atom: true,
  addAttributes: () => ({ citationKey: { default: '' }, suffix: { default: null } }),
  parseHTML: () => [{ tag: 'span[data-citation]' }],
  renderHTML: ({ node, HTMLAttributes }) => ['span', mergeAttributes(HTMLAttributes, { 'data-citation': node.attrs.citationKey, class: 'library-citation' }), `[@${node.attrs.citationKey}${node.attrs.suffix ? `, ${node.attrs.suffix}` : ''}]`],
  addNodeView() { return ReactNodeViewRenderer(CitationNodeView); },
});

const FootnoteReferenceNodeView: React.FC<any> = ({ node, updateAttributes, editor }) => {
  const editable = useEditorState({ editor, selector: ({ editor: current }) => current.isEditable });
  const [label, setLabel] = useState(node.attrs.label || '');
  const form = <div className="library-atom-form"><Input size="small" value={label} onChange={event => setLabel(event.target.value)} placeholder="Footnote label" /><Button size="small" type="primary" onClick={() => { if (label.trim()) updateAttributes({ label: label.trim() }); }}>Save</Button></div>;
  return <NodeViewWrapper as="sup" className="library-footnote-reference" contentEditable={false}>
    {editable ? <Popover content={form} trigger="click"><button type="button" className="library-inline-atom-button" aria-label="Edit footnote reference">[{node.attrs.label}]</button></Popover> : `[${node.attrs.label}]`}
  </NodeViewWrapper>;
};

const FootnoteReference = Node.create({
  name: 'footnoteReference', group: 'inline', inline: true, atom: true,
  addAttributes: () => ({ label: { default: '' } }),
  parseHTML: () => [{ tag: 'sup[data-footnote-reference]' }],
  renderHTML: ({ node, HTMLAttributes }) => ['sup', mergeAttributes(HTMLAttributes, { 'data-footnote-reference': node.attrs.label, class: 'library-footnote-reference' }), `[${node.attrs.label}]`],
  addNodeView() { return ReactNodeViewRenderer(FootnoteReferenceNodeView); },
});

const FootnoteDefinitionNodeView: React.FC<any> = ({ node, updateAttributes, editor }) => {
  const editable = useEditorState({ editor, selector: ({ editor: current }) => current.isEditable });
  return <NodeViewWrapper className="library-footnote-definition">
    <div contentEditable={false}>
      {editable
        ? <Input size="small" aria-label="Footnote label" value={node.attrs.label} onChange={event => updateAttributes({ label: event.target.value })} />
        : <span>[{node.attrs.label}]</span>}
    </div>
    <NodeViewContent as="div" />
  </NodeViewWrapper>;
};

const FootnoteDefinition = Node.create({
  name: 'footnoteDefinition', group: 'block', content: 'block+', defining: true,
  addAttributes: () => ({ label: { default: '' } }),
  parseHTML: () => [{ tag: 'aside[data-footnote-definition]' }],
  renderHTML: ({ node, HTMLAttributes }) => ['aside', mergeAttributes(HTMLAttributes, { 'data-footnote-definition': node.attrs.label, class: 'library-footnote-definition' }), ['span', { contenteditable: 'false' }, `[${node.attrs.label}]`], ['div', 0]],
  addNodeView() { return ReactNodeViewRenderer(FootnoteDefinitionNodeView); },
});

const DefinitionList = Node.create({ name: 'definitionList', group: 'block', content: '(definitionTerm definitionDescription+)+', parseHTML: () => [{ tag: 'dl' }], renderHTML: ({ HTMLAttributes }) => ['dl', HTMLAttributes, 0] });
const DefinitionTerm = Node.create({ name: 'definitionTerm', content: 'inline*', defining: true, parseHTML: () => [{ tag: 'dt' }], renderHTML: ({ HTMLAttributes }) => ['dt', HTMLAttributes, 0] });
const DefinitionDescription = Node.create({ name: 'definitionDescription', content: 'block+', defining: true, parseHTML: () => [{ tag: 'dd' }], renderHTML: ({ HTMLAttributes }) => ['dd', HTMLAttributes, 0] });

const renderMath = (source: string, displayMode: boolean) => {
  let html = '';
  try { html = katex.renderToString(source, { displayMode, throwOnError: false, strict: 'warn' }); }
  catch { html = source; }
  return html;
};

const InlineMathNodeView: React.FC<any> = ({ node, updateAttributes, editor }) => {
  const editable = useEditorState({ editor, selector: ({ editor: current }) => current.isEditable });
  const [source, setSource] = useState(node.attrs.source || '');
  const form = <div className="library-atom-form"><Input size="small" value={source} onChange={event => setSource(event.target.value)} placeholder="LaTeX formula" /><Button size="small" type="primary" onClick={() => { if (source.trim()) updateAttributes({ source: source.trim() }); }}>Save formula</Button></div>;
  return <NodeViewWrapper as="span" className="library-inlineMath" data-math-source={node.attrs.source} contentEditable={false}>
    <span dangerouslySetInnerHTML={{ __html: renderMath(node.attrs.source || '', false) }} />
    {editable && <Popover content={form} trigger="click"><button type="button" className="library-inline-edit" aria-label="Edit inline formula"><FontAwesomeIcon icon={faPen} /></button></Popover>}
  </NodeViewWrapper>;
};

const InlineMath = Node.create({
  name: 'inlineMath', group: 'inline', inline: true, atom: true,
  addAttributes: () => ({ source: { default: '' } }), parseHTML: () => [{ tag: 'span[data-math-source]' }],
  renderHTML: ({ HTMLAttributes }) => ['span', mergeAttributes(HTMLAttributes, { class: 'library-inlineMath' })],
  addNodeView() { return ReactNodeViewRenderer(InlineMathNodeView); },
});

const MathBlockNodeView: React.FC<any> = ({ node, updateAttributes, editor }) => {
  const editable = useEditorState({ editor, selector: ({ editor: current }) => current.isEditable });
  const [editing, setEditing] = useState(false);
  const [source, setSource] = useState(node.attrs.source || '');
  useEffect(() => setSource(node.attrs.source || ''), [node.attrs.source]);
  useEffect(() => { if (!editable) setEditing(false); }, [editable]);
  return <NodeViewWrapper className="library-mathBlock" data-math-source={node.attrs.source}>
    <div className="library-special-block-heading" contentEditable={false}>
      <span>LaTeX formula</span>
      {editable && <Button size="small" type="text" icon={<FontAwesomeIcon icon={faPen} />} onClick={() => setEditing(value => !value)}>{editing ? 'Preview' : 'Edit source'}</Button>}
    </div>
    {editing && editable ? <div className="library-special-source" contentEditable={false}>
      <Input.TextArea value={source} autoSize={{ minRows: 3, maxRows: 10 }} onChange={event => setSource(event.target.value)} />
      <Button size="small" type="primary" onClick={() => { updateAttributes({ source: source.trim() }); setEditing(false); }}>Save formula</Button>
    </div> : <div className="library-math-preview" contentEditable={false} dangerouslySetInnerHTML={{ __html: renderMath(node.attrs.source || '', true) }} />}
  </NodeViewWrapper>;
};

const MathBlock = Node.create({
  name: 'mathBlock', group: 'block', atom: true,
  addAttributes: () => ({ source: { default: '' } }), parseHTML: () => [{ tag: 'div[data-math-source]' }],
  renderHTML: ({ HTMLAttributes }) => ['div', mergeAttributes(HTMLAttributes, { class: 'library-mathBlock' })],
  addNodeView() { return ReactNodeViewRenderer(MathBlockNodeView); },
});

const MermaidNodeView: React.FC<any> = ({ node, updateAttributes, editor }) => {
  const editable = useEditorState({ editor, selector: ({ editor: current }) => current.isEditable });
  const [svg, setSvg] = useState('');
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [source, setSource] = useState(node.attrs.source || '');
  const [expanded, setExpanded] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [panning, setPanning] = useState(false);
  const overlayViewport = useRef<HTMLDivElement | null>(null);
  const panStart = useRef<{ pointerId: number; x: number; y: number; left: number; top: number } | null>(null);
  const renderId = useRef(`mermaid-${crypto.randomUUID()}`);
  const handleNativeZoom = useCallback((event: WheelEvent) => {
    if (!event.ctrlKey && !event.metaKey) return;
    event.preventDefault();
    setZoom(value => Math.min(5, Math.max(.5, value * Math.exp(-event.deltaY * .01))));
  }, []);
  const setOverlayViewport = useCallback((viewport: HTMLDivElement | null) => {
    overlayViewport.current?.removeEventListener('wheel', handleNativeZoom);
    overlayViewport.current = viewport;
    viewport?.addEventListener('wheel', handleNativeZoom, { passive: false });
  }, [handleNativeZoom]);
  useEffect(() => setSource(node.attrs.source || ''), [node.attrs.source]);
  useEffect(() => { if (!editable) setEditing(false); }, [editable]);
  useEffect(() => {
    let alive = true;
    import('mermaid').then(({ default: mermaid }) => {
      mermaid.initialize({ startOnLoad: false, securityLevel: 'strict', theme: 'dark' });
      return mermaid.render(renderId.current, node.attrs.source || '');
    }).then(result => { if (alive) { setSvg(result.svg); setError(''); } })
      .catch(renderError => { if (alive) { setSvg(''); setError(renderError?.message || 'Diagram could not be rendered'); } });
    return () => { alive = false; };
  }, [node.attrs.source]);
  useEffect(() => {
    if (!expanded) return;
    const frame = requestAnimationFrame(() => {
      const viewport = overlayViewport.current;
      if (!viewport) return;
      viewport.scrollLeft = Math.max(0, (viewport.scrollWidth - viewport.clientWidth) / 2);
      viewport.scrollTop = Math.max(0, (viewport.scrollHeight - viewport.clientHeight) / 2);
    });
    return () => cancelAnimationFrame(frame);
  }, [expanded, zoom]);

  const updateZoom = (nextZoom: number) => setZoom(Math.min(5, Math.max(.5, nextZoom)));
  const startPanning = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    const viewport = overlayViewport.current;
    if (!viewport) return;
    panStart.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, left: viewport.scrollLeft, top: viewport.scrollTop };
    viewport.setPointerCapture(event.pointerId);
    setPanning(true);
  };
  const movePanning = (event: React.PointerEvent<HTMLDivElement>) => {
    const start = panStart.current;
    const viewport = overlayViewport.current;
    if (!start || !viewport || start.pointerId !== event.pointerId) return;
    event.preventDefault();
    viewport.scrollLeft = start.left - (event.clientX - start.x);
    viewport.scrollTop = start.top - (event.clientY - start.y);
  };
  const stopPanning = (event: React.PointerEvent<HTMLDivElement>) => {
    if (panStart.current?.pointerId !== event.pointerId) return;
    panStart.current = null;
    setPanning(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  };
  const openExpanded = () => { setZoom(1); setExpanded(true); };
  return <NodeViewWrapper className="library-mermaid-block" data-drag-handle>
    <div className="library-special-block-heading" contentEditable={false}>
      <span>Mermaid diagram</span>
      <div className="library-special-block-actions">
        {!editing && <Button size="small" type="text" disabled={!svg} icon={<FontAwesomeIcon icon={faExpand} />} onClick={openExpanded}>Expand</Button>}
        {editable && <Button size="small" type="text" icon={<FontAwesomeIcon icon={faPen} />} onClick={() => setEditing(value => !value)}>{editing ? 'Preview' : 'Edit source'}</Button>}
      </div>
    </div>
    {editing && editable ? <div className="library-special-source" contentEditable={false}>
      <Input.TextArea value={source} autoSize={{ minRows: 5, maxRows: 16 }} onChange={event => setSource(event.target.value)} />
      <Button size="small" type="primary" onClick={() => { updateAttributes({ source: source.trim() }); setEditing(false); }}>Save diagram</Button>
    </div> : error ? <div className="library-special-error" contentEditable={false}><span>{error}</span><pre>{node.attrs.source}</pre></div> : svg ? <div
      className="library-mermaid-preview"
      contentEditable={false}
      role="button"
      tabIndex={0}
      aria-label="Open expanded Mermaid diagram"
      title="Open expanded diagram"
      onClick={openExpanded}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          openExpanded();
        }
      }}
      dangerouslySetInnerHTML={{ __html: svg }}
    /> : <div className="library-mermaid-loading" contentEditable={false}>Rendering diagram…</div>}
    <Modal title="Mermaid diagram" open={expanded} width={1200} footer={null} onCancel={() => setExpanded(false)} destroyOnHidden>
      <div className="library-diagram-overlay-toolbar">
        <span className="library-diagram-overlay-hint">Pinch or Ctrl/⌘ + scroll to zoom · drag or two-finger scroll to pan</span>
        <span>{Math.round(zoom * 100)}%</span>
        <Tooltip title="Zoom out"><Button aria-label="Zoom diagram out" icon={<FontAwesomeIcon icon={faMagnifyingGlassMinus} />} disabled={zoom <= .5} onClick={() => updateZoom(zoom - .25)} /></Tooltip>
        <Tooltip title="Reset zoom"><Button aria-label="Reset diagram zoom" icon={<FontAwesomeIcon icon={faRotateLeft} />} onClick={() => setZoom(1)} /></Tooltip>
        <Tooltip title="Zoom in"><Button aria-label="Zoom diagram in" icon={<FontAwesomeIcon icon={faMagnifyingGlassPlus} />} disabled={zoom >= 5} onClick={() => updateZoom(zoom + .25)} /></Tooltip>
      </div>
      <div
        ref={setOverlayViewport}
        className={`library-diagram-overlay-viewport${panning ? ' is-panning' : ''}`}
        onPointerDown={startPanning}
        onPointerMove={movePanning}
        onPointerUp={stopPanning}
        onPointerCancel={stopPanning}
      >
        <div className="library-diagram-overlay-stage">
          <div className="library-diagram-overlay-canvas" style={{ zoom }} dangerouslySetInnerHTML={{ __html: svg }} />
        </div>
      </div>
    </Modal>
  </NodeViewWrapper>;
};

const MermaidBlock = Node.create({
  name: 'mermaidBlock', group: 'block', atom: true, draggable: true,
  addAttributes: () => ({ source: { default: '' } }), parseHTML: () => [{ tag: 'div[data-mermaid-source]' }],
  renderHTML: ({ HTMLAttributes }) => ['div', mergeAttributes(HTMLAttributes, { 'data-mermaid-source': HTMLAttributes.source, class: 'library-mermaid-block' })],
  addNodeView() { return ReactNodeViewRenderer(MermaidNodeView); },
});

const ImageNodeView: React.FC<any> = ({ node, updateAttributes, editor }) => {
  const editable = useEditorState({ editor, selector: ({ editor: current }) => current.isEditable });
  const [src, setSrc] = useState(node.attrs.src || '');
  const [alt, setAlt] = useState(node.attrs.alt || '');
  const [title, setTitle] = useState(node.attrs.title || '');
  const form = <div className="library-atom-form library-image-form">
    <Input size="small" value={src} onChange={event => setSrc(event.target.value)} placeholder="Image URL" />
    <Input size="small" value={alt} onChange={event => setAlt(event.target.value)} placeholder="Alternative text" />
    <Input size="small" value={title} onChange={event => setTitle(event.target.value)} placeholder="Title (optional)" />
    <Button size="small" type="primary" onClick={() => updateAttributes({ src: src.trim(), alt: alt.trim() || null, title: title.trim() || null })}>Save image</Button>
  </div>;
  return <NodeViewWrapper as="span" className="library-editable-image" contentEditable={false}>
    <img src={node.attrs.src} alt={node.attrs.alt || ''} title={node.attrs.title || undefined} />
    {editable && <Popover content={form} trigger="click"><Button className="library-image-edit" size="small" aria-label="Edit image" icon={<FontAwesomeIcon icon={faPen} />}>Edit image</Button></Popover>}
  </NodeViewWrapper>;
};

const EditableImage = Image.extend({ addNodeView() { return ReactNodeViewRenderer(ImageNodeView); } });

const tableAlignmentAttribute = {
  default: null,
  parseHTML: (element: HTMLElement) => element.style.textAlign || null,
  renderHTML: (attributes: { textAlign?: string | null }) => attributes.textAlign
    ? { style: `text-align: ${attributes.textAlign}` }
    : {},
};

const AlignedTableCell = TableCell.extend({
  addAttributes() { return { ...this.parent?.(), textAlign: tableAlignmentAttribute }; },
});

const AlignedTableHeader = TableHeader.extend({
  addAttributes() { return { ...this.parent?.(), textAlign: tableAlignmentAttribute }; },
});

export function createNoteContentExtensions(noteId: string, editable: boolean) {
  return [
    StarterKit.configure({ undoRedo: false, link: false, underline: false, codeBlock: false, heading: { levels: [1, 2, 3, 4, 5, 6] } }),
    CopyableCodeBlock,
    Underline,
    Highlight,
    RichLink.configure({ openOnClick: !editable, autolink: true, defaultProtocol: 'https' }),
    EditableImage.configure({ inline: true, allowBase64: false }),
    Citation,
    FootnoteReference,
    FootnoteDefinition,
    DefinitionList,
    DefinitionTerm,
    DefinitionDescription,
    InlineMath,
    MathBlock,
    MermaidBlock,
    TaskList,
    TaskItem.configure({
      nested: true,
      // In view mode this leaves the checkbox's temporary DOM state alone
      // without changing the collaborative document.
      onReadOnlyChecked: () => true,
    }),
    Table.configure({ resizable: true, allowTableNodeSelection: true }),
    TableRow,
    AlignedTableHeader,
    AlignedTableCell,
    Placeholder.configure({ placeholder: 'Start writing…' }),
    createSecretBlock(noteId),
  ];
}

function legacyBlocksToJson(blocks: NoteBlock[]) {
  return {
    type: 'doc',
    content: blocks.map(block => {
      if (block.editorNode && typeof block.editorNode.type === 'string') return block.editorNode;
      const text = block.text ? [{ type: 'text', text: block.text }] : undefined;
      if (block.type === 'heading') return { type: 'heading', attrs: { level: 2 }, content: text };
      if (block.type === 'code') return { type: 'codeBlock', attrs: { language: block.language || null }, content: text };
      if (block.type === 'checklist') return { type: 'taskList', content: [{ type: 'taskItem', attrs: { checked: Boolean(block.checked) }, content: [{ type: 'paragraph', content: text }] }] };
      if (block.type === 'secret') return { type: 'secretBlock', attrs: { blockId: block.id, label: block.text || 'Protected value', hasValue: false } };
      return { type: 'paragraph', content: text };
    }),
  };
}

interface ToolbarProps { editor: Editor; onFind: () => void }
const EditorToolbar: React.FC<ToolbarProps> = ({ editor, onFind }) => {
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkTitle, setLinkTitle] = useState('');
  const [specialDialog, setSpecialDialog] = useState<'image' | 'inline-math' | 'math-block' | 'citation' | null>(null);
  const [specialPrimary, setSpecialPrimary] = useState('');
  const [specialSecondary, setSpecialSecondary] = useState('');
  const [specialTertiary, setSpecialTertiary] = useState('');
  const promptForLink = () => {
    const attributes = editor.getAttributes('link');
    setLinkUrl(attributes.href || '');
    setLinkTitle(attributes.title || '');
    setLinkOpen(true);
  };
  const button = (label: string, icon: any, active: boolean, action: () => void, shortcut?: string) => (
    <Tooltip title={`${label}${shortcut ? ` · ${shortcut}` : ''}`} key={label}>
      <Button aria-label={label} type="text" className={active ? 'is-active' : ''} icon={<FontAwesomeIcon icon={icon} />} onClick={action} />
    </Tooltip>
  );
  const inTable = editor.isActive('table');
  const activeHeading = ([1, 2, 3, 4, 5, 6] as const).find(level => editor.isActive('heading', { level }));
  const tableMenu = {
    items: [
      { key: 'add-row-before', label: 'Add row above' },
      { key: 'add-row-after', label: 'Add row below' },
      { key: 'add-column-before', label: 'Add column before' },
      { key: 'add-column-after', label: 'Add column after' },
      { type: 'divider' as const },
      { key: 'delete-row', label: 'Delete row' },
      { key: 'delete-column', label: 'Delete column' },
      { key: 'toggle-header-row', label: 'Toggle header row' },
      { type: 'divider' as const },
      { key: 'delete-table', label: 'Delete table', danger: true },
    ],
    onClick: ({ key }: { key: string }) => {
      const chain = editor.chain().focus();
      if (key === 'add-row-before') chain.addRowBefore().run();
      else if (key === 'add-row-after') chain.addRowAfter().run();
      else if (key === 'add-column-before') chain.addColumnBefore().run();
      else if (key === 'add-column-after') chain.addColumnAfter().run();
      else if (key === 'delete-row') chain.deleteRow().run();
      else if (key === 'delete-column') chain.deleteColumn().run();
      else if (key === 'toggle-header-row') chain.toggleHeaderRow().run();
      else if (key === 'delete-table') chain.deleteTable().run();
    },
  };
  const insertSpecial = (key: string) => {
    if (key === 'image' || key === 'inline-math' || key === 'math-block' || key === 'citation') {
      setSpecialDialog(key);
      setSpecialPrimary(key === 'inline-math' ? 'E = mc^2' : key === 'math-block' ? '\\int_0^1 x^2\\,dx' : key === 'citation' ? 'source-key' : '');
      setSpecialSecondary('');
      setSpecialTertiary('');
    } else if (key === 'mermaid') {
      editor.chain().focus().insertContent({ type: 'mermaidBlock', attrs: { source: 'flowchart LR\n  A[Start] --> B[Finish]' } }).run();
    } else if (key === 'footnote') {
      const label = `note-${Date.now().toString(36)}`;
      editor.chain().focus().insertContent({ type: 'footnoteReference', attrs: { label } }).run();
      editor.commands.insertContentAt(editor.state.doc.content.size, { type: 'footnoteDefinition', attrs: { label }, content: [{ type: 'paragraph', content: [{ type: 'text', text: 'Footnote text' }] }] });
    } else if (key === 'definition-list') {
      editor.chain().focus().insertContent({ type: 'definitionList', content: [
        { type: 'definitionTerm', content: [{ type: 'text', text: 'Term' }] },
        { type: 'definitionDescription', content: [{ type: 'paragraph', content: [{ type: 'text', text: 'Definition' }] }] },
      ] }).run();
    }
  };
  const submitSpecial = () => {
    if (!specialDialog || !specialPrimary.trim()) return;
    const chain = editor.chain().focus();
    if (specialDialog === 'image') chain.insertContent({ type: 'image', attrs: { src: specialPrimary.trim(), alt: specialSecondary.trim() || null, title: specialTertiary.trim() || null } }).run();
    else if (specialDialog === 'inline-math') chain.insertContent({ type: 'inlineMath', attrs: { source: specialPrimary.trim() } }).run();
    else if (specialDialog === 'math-block') chain.insertContent({ type: 'mathBlock', attrs: { source: specialPrimary.trim() } }).run();
    else if (specialDialog === 'citation') chain.insertContent({ type: 'citation', attrs: { citationKey: specialPrimary.trim(), suffix: specialSecondary.trim() || null } }).run();
    setSpecialDialog(null);
  };
  const specialMenu = {
    items: [
      { key: 'image', label: 'Image' },
      { key: 'inline-math', label: 'Inline formula' },
      { key: 'math-block', label: 'Display formula' },
      { key: 'mermaid', label: 'Mermaid diagram' },
      { key: 'citation', label: 'Citation' },
      { key: 'footnote', label: 'Footnote' },
      { key: 'definition-list', label: 'Definition list' },
    ],
    onClick: ({ key }: { key: string }) => insertSpecial(key),
  };
  return (
    <div className="library-editor-toolbar">
      <Select
        value={activeHeading ? `h${activeHeading}` : 'paragraph'}
        onChange={value => {
          if (value === 'paragraph') editor.chain().focus().setParagraph().run();
          else editor.chain().focus().toggleHeading({ level: Number(value.slice(1)) as 1 | 2 | 3 | 4 | 5 | 6 }).run();
        }}
        options={[{ value: 'paragraph', label: 'Normal text' }, ...[1, 2, 3, 4, 5, 6].map(level => ({ value: `h${level}`, label: `Heading ${level}` }))]}
      />
      <Divider type="vertical" />
      {button('Undo', faUndo, false, () => editor.commands.undo(), '⌘Z')}
      {button('Redo', faRedo, false, () => editor.commands.redo(), '⇧⌘Z')}
      {button('Find in note', faMagnifyingGlass, false, onFind, '⌘F')}
      <Divider type="vertical" />
      {button('Bold', faBold, editor.isActive('bold'), () => editor.chain().focus().toggleBold().run(), '⌘B')}
      {button('Italic', faItalic, editor.isActive('italic'), () => editor.chain().focus().toggleItalic().run(), '⌘I')}
      {button('Underline', faUnderline, editor.isActive('underline'), () => editor.chain().focus().toggleUnderline().run(), '⌘U')}
      {button('Strikethrough', faStrikethrough, editor.isActive('strike'), () => editor.chain().focus().toggleStrike().run())}
      {button('Highlight', faHighlighter, editor.isActive('highlight'), () => editor.chain().focus().toggleHighlight().run())}
      {button('Link', faLink, editor.isActive('link'), promptForLink)}
      <Divider type="vertical" />
      {button('Bullet list', faListUl, editor.isActive('bulletList'), () => editor.chain().focus().toggleBulletList().run())}
      {button('Numbered list', faListOl, editor.isActive('orderedList'), () => editor.chain().focus().toggleOrderedList().run())}
      {button('Checklist', faCheckSquare, editor.isActive('taskList'), () => editor.chain().focus().toggleTaskList().run())}
      {button('Quote', faQuoteLeft, editor.isActive('blockquote'), () => editor.chain().focus().toggleBlockquote().run())}
      {button('Code block', faCode, editor.isActive('codeBlock'), () => editor.chain().focus().toggleCodeBlock().run())}
      <Dropdown key="table" menu={tableMenu} disabled={!inTable} trigger={['click']}>
        <Tooltip title={inTable ? 'Table options' : 'Insert table'}>
          <Button
            aria-label={inTable ? 'Table options' : 'Insert table'}
            type="text"
            className={inTable ? 'is-active' : ''}
            icon={<FontAwesomeIcon icon={faTable} />}
            onClick={() => { if (!inTable) editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(); }}
          />
        </Tooltip>
      </Dropdown>
      {button('Secret', faKey, false, () => editor.chain().focus().insertContent({ type: 'secretBlock', attrs: { blockId: crypto.randomUUID(), label: 'Protected value', hasValue: false } }).run())}
      {button('Divider', faMinus, false, () => editor.chain().focus().setHorizontalRule().run())}
      <Dropdown key="insert-special" menu={specialMenu} trigger={['click']}>
        <Tooltip title="More insert options">
          <Button aria-label="More insert options" type="text" icon={<FontAwesomeIcon icon={faEllipsis} />} />
        </Tooltip>
      </Dropdown>
      <Modal title="Edit link" open={linkOpen} okText="Apply link" onCancel={() => setLinkOpen(false)} onOk={() => {
        if (linkUrl.trim()) editor.chain().focus().extendMarkRange('link').setLink({ href: linkUrl.trim(), title: linkTitle.trim() || null }).run();
        else editor.chain().focus().extendMarkRange('link').unsetLink().run();
        setLinkOpen(false);
      }}>
        <div className="library-modal-form"><Input value={linkUrl} onChange={event => setLinkUrl(event.target.value)} placeholder="https://example.com or /relative-path" /><Input value={linkTitle} onChange={event => setLinkTitle(event.target.value)} placeholder="Title (optional)" /></div>
      </Modal>
      <Modal title={specialDialog === 'image' ? 'Insert image' : specialDialog === 'inline-math' ? 'Insert inline formula' : specialDialog === 'math-block' ? 'Insert display formula' : 'Insert citation'} open={Boolean(specialDialog)} okText="Insert" okButtonProps={{ disabled: !specialPrimary.trim() }} onCancel={() => setSpecialDialog(null)} onOk={submitSpecial}>
        <div className="library-modal-form">
          {specialDialog === 'image' ? <>
            <Input value={specialPrimary} onChange={event => setSpecialPrimary(event.target.value)} placeholder="Image URL" />
            <Input value={specialSecondary} onChange={event => setSpecialSecondary(event.target.value)} placeholder="Alternative text" />
            <Input value={specialTertiary} onChange={event => setSpecialTertiary(event.target.value)} placeholder="Title (optional)" />
          </> : specialDialog === 'citation' ? <>
            <Input value={specialPrimary} onChange={event => setSpecialPrimary(event.target.value)} placeholder="Citation key" />
            <Input value={specialSecondary} onChange={event => setSpecialSecondary(event.target.value)} placeholder="Page or section (optional)" />
          </> : <Input.TextArea value={specialPrimary} onChange={event => setSpecialPrimary(event.target.value)} autoSize={{ minRows: specialDialog === 'math-block' ? 4 : 2, maxRows: 10 }} placeholder="LaTeX source" />}
        </div>
      </Modal>
    </div>
  );
};

interface RichNoteEditorProps {
  noteId: string;
  doc: Y.Doc;
  provider: { awareness: Awareness };
  editable: boolean;
  user: { name: string; color: string };
  legacyContent?: NoteBlock[];
  knownSecretBlockIds: string[];
  seedImportedContent?: boolean;
  onImportedContentSeeded?: () => void;
}

const RichNoteEditor: React.FC<RichNoteEditorProps> = ({ noteId, doc, provider, editable, user, legacyContent = [], knownSecretBlockIds, seedImportedContent = false, onImportedContentSeeded }) => {
  const migrated = useRef(false);
  const knownSecrets = useRef(new Set(knownSecretBlockIds));
  const [findOpen, setFindOpen] = useState(false);
  const [findFocusRequest, setFindFocusRequest] = useState(0);
  const collaborationUser = useMemo(() => ({ name: user.name, color: user.color }), [user.color, user.name]);
  const extensions = useMemo(() => [
    ...createNoteContentExtensions(noteId, editable),
    FindAndReplace.configure({ injectCSS: false, searchDebounceMs: 120 }),
    Collaboration.configure({ document: doc }),
    CollaborationCaret.configure({ provider, user: collaborationUser }),
  ], [collaborationUser, doc, editable, noteId, provider]);

  const editor = useEditor({
    extensions,
    editable,
    autofocus: editable ? 'end' : false,
    immediatelyRender: false,
    editorProps: {
      attributes: { class: 'library-prose', spellcheck: 'true' },
      handlePaste: (view, event) => {
        const clipboard = event.clipboardData;
        const markdown = clipboard?.getData('text/plain') || '';
        if (!markdown || !looksLikeMarkdown(markdown)) return false;
        if (new Blob([markdown]).size > MAX_MARKDOWN_FILE_BYTES) {
          message.error('Markdown paste must be 1 MB or smaller');
          return true;
        }
        try {
          const imported = parseMarkdownNote(markdown, 'pasted.md', { preserveFirstHeading: true });
          const nodes = imported.blocks
            .map(block => block.editorNode)
            .filter((node): node is Record<string, unknown> => Boolean(node?.type))
            .map(node => view.state.schema.nodeFromJSON(node));
          if (!nodes.length) return false;
          const slice = new Slice(Fragment.fromArray(nodes), 0, 0);
          view.dispatch(view.state.tr.replaceSelection(slice).scrollIntoView());
          if (imported.secrets.length) {
            Promise.all(imported.secrets.map(secret => saveNoteSecretBlock(noteId, secret.blockId, secret.label, secret.value)))
              .catch(() => message.warning('Markdown was pasted, but one or more protected values could not be saved'));
          }
          return true;
        } catch {
          return false;
        }
      },
    },
    onUpdate: ({ editor: current }) => {
      const currentIds = new Set<string>();
      current.state.doc.descendants(node => { if (node.type.name === 'secretBlock' && node.attrs.blockId) currentIds.add(node.attrs.blockId); });
      knownSecrets.current.forEach(blockId => {
        if (!currentIds.has(blockId)) deleteNoteSecretBlock(noteId, blockId).catch(() => undefined);
      });
      knownSecrets.current = currentIds;
    },
  });

  useEffect(() => { editor?.setEditable(editable); }, [editable, editor]);
  const openFind = useCallback(() => {
    setFindOpen(true);
    setFindFocusRequest(value => value + 1);
  }, []);
  useEffect(() => {
    const handleFindShortcut = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.altKey || (!event.metaKey && !event.ctrlKey) || event.key.toLowerCase() !== 'f') return;
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest('.ant-modal-root, .ant-drawer, .library-inline-secret-value')) return;
      event.preventDefault();
      openFind();
    };
    window.addEventListener('keydown', handleFindShortcut);
    return () => window.removeEventListener('keydown', handleFindShortcut);
  }, [openFind]);
  useEffect(() => {
    if (!editor || migrated.current || legacyContent.length === 0) return;
    if (!seedImportedContent && !editor.isEmpty) return;
    migrated.current = true;
    const seeded = editor.commands.setContent(legacyBlocksToJson(legacyContent));
    if (seeded && seedImportedContent) onImportedContentSeeded?.();
  }, [editor, legacyContent, onImportedContentSeeded, seedImportedContent]);

  if (!editor) return <div className="library-editor-loading">Preparing document…</div>;
  return (
    <div className={`library-rich-editor ${editable ? 'is-editing' : 'is-viewing'} ${findOpen ? 'has-find-panel' : ''}`}>
      {editable && <EditorToolbar editor={editor} onFind={openFind} />}
      {findOpen && <NoteFindReplace editor={editor} editable={editable} focusRequest={findFocusRequest} onClose={() => setFindOpen(false)} />}
      <div className="library-editor-document"><EditorContent editor={editor} /></div>
    </div>
  );
};

export default RichNoteEditor;
